<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useSortable } from '@vueuse/integrations/useSortable'
import { VIcon } from '@/modules/shared/components'
import { useTeamMemberStore } from '@/modules/connecting/stores/team-member-store'
import { useWorkspaceOnboardingStore } from '../../stores/workspace-onboarding-store'
import type { TeamMember } from '@/modules/connecting/types'

const onboardingStore = useWorkspaceOnboardingStore()
const teamMemberStore = useTeamMemberStore()

const candidateId = computed(() => onboardingStore.workspace?.connecting_candidate_id?.toString())

const linkedinUrl = ref('')
const importing = ref(false)
const importError = ref('')
const sortableRef = ref<HTMLElement | null>(null)
const localMembers = ref<TeamMember[]>([])

const syncLocalMembers = () => {
  localMembers.value = [...teamMemberStore.members]
}

const setupSortable = () => {
  if (!sortableRef.value) return
  useSortable(sortableRef, localMembers, {
    handle: '.drag-handle',
    animation: 150,
    onEnd: async (evt) => {
      const { oldIndex, newIndex } = evt
      if (oldIndex !== undefined && newIndex !== undefined && oldIndex !== newIndex && candidateId.value) {
        const item = localMembers.value.splice(oldIndex, 1)[0]
        localMembers.value.splice(newIndex, 0, item)
        const memberIds = localMembers.value.map((m) => m.id)
        await teamMemberStore.reorderTeamMembers(candidateId.value, memberIds)
      }
    },
  })
}

onMounted(async () => {
  if (candidateId.value) {
    await teamMemberStore.fetchTeamMembers(candidateId.value)
    syncLocalMembers()
    await nextTick()
    setupSortable()
  }
})

watch(candidateId, async (newId) => {
  if (newId) {
    teamMemberStore.reset()
    await teamMemberStore.fetchTeamMembers(newId)
    syncLocalMembers()
    await nextTick()
    setupSortable()
  }
})

const addLinkedin = async () => {
  const url = linkedinUrl.value.trim()
  if (!url || !candidateId.value) return

  importError.value = ''
  importing.value = true
  try {
    const result = await teamMemberStore.lookupLinkedin(candidateId.value, url)
    if (!result?.name) {
      importError.value = 'Could not fetch profile. Please check the URL and try again.'
      return
    }

    const formData = new FormData()
    formData.append('team_member[name]', result.name)
    formData.append('team_member[title]', result.title || '')
    formData.append('team_member[bio]', result.bio || '')
    formData.append('team_member[linkedin_url]', url)
    if (result.photo_url) {
      formData.append('team_member[photo_url]', result.photo_url)
    }

    await teamMemberStore.createTeamMember(candidateId.value, formData)
    syncLocalMembers()
    linkedinUrl.value = ''
  } catch {
    importError.value = 'Could not fetch profile. Please check the URL and try again.'
  } finally {
    importing.value = false
  }
}

const removeMember = async (member: TeamMember) => {
  if (!candidateId.value) return
  await teamMemberStore.deleteTeamMember(candidateId.value, member.id)
  syncLocalMembers()
}

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') addLinkedin()
}

// Team members are saved individually — nothing to submit on Continue
const submit = async (): Promise<boolean> => true

defineExpose({ submit })
</script>

<template>
  <div class="flex flex-1 flex-col">
    <!-- Member Cards -->
    <div v-if="localMembers.length" ref="sortableRef" class="mb-4 space-y-2">
      <div
        v-for="member in localMembers"
        :key="member.id"
        class="flex items-center gap-3 rounded-[10px] border border-gray-200 bg-white px-4 py-3"
      >
        <div class="drag-handle cursor-move text-gray-300 hover:text-gray-500">
          <VIcon name="Move" class="h-4 w-4" />
        </div>
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#3D8A9D]/10 text-sm font-semibold text-[#3D8A9D]"
        >
          <img v-if="member.photo_url" :src="member.photo_url" alt="" class="h-full w-full object-cover" />
          <span v-else>{{ member.initials }}</span>
        </div>
        <div class="min-w-0 flex-1">
          <div class="text-sm font-medium text-gray-800">{{ member.name }}</div>
          <div class="text-xs text-gray-400">{{ member.title || 'Team Member' }}</div>
          <div
            v-if="member.linkedin_url"
            class="mt-0.5 flex items-center gap-1 overflow-hidden text-[11px] text-[#0A66C2]"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#0A66C2" class="shrink-0">
              <path
                d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
              />
            </svg>
            <span class="truncate">{{ member.linkedin_url }}</span>
          </div>
        </div>
        <button
          type="button"
          class="p-1 text-xl leading-none text-gray-300 transition hover:text-red-500"
          @click="removeMember(member)"
        >
          &times;
        </button>
      </div>
    </div>

    <!-- LinkedIn Import Row -->
    <div class="flex items-center gap-2">
      <div class="relative flex-1">
        <input
          v-model="linkedinUrl"
          type="text"
          placeholder="https://linkedin.com/in/firstname-lastname"
          class="w-full rounded-md border border-gray-300 py-3 pl-10 pr-4 text-sm text-gray-800 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
          :disabled="importing"
          @keydown="onKeydown"
        />
        <div class="absolute left-3.5 top-1/2 -translate-y-1/2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#0A66C2">
            <path
              d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
            />
          </svg>
        </div>
      </div>
      <button
        type="button"
        class="shrink-0 rounded-[10px] bg-[#3D8A9D] px-5 py-3 text-sm font-medium text-white transition hover:bg-[#357880] disabled:cursor-default disabled:bg-[#3D8A9D]/50"
        :disabled="importing || !linkedinUrl.trim()"
        @click="addLinkedin"
      >
        Import
      </button>
    </div>

    <!-- Loading indicator -->
    <div
      v-if="importing"
      class="mt-3 flex items-center gap-2.5 rounded-lg bg-[#3D8A9D]/5 px-4 py-3 text-sm text-[#3D8A9D]"
    >
      <div class="h-4 w-4 animate-spin rounded-full border-2 border-[#3D8A9D]/20 border-t-[#3D8A9D]" />
      Fetching profile from LinkedIn...
    </div>

    <!-- Error -->
    <p v-if="importError" class="mt-2 text-sm text-red-500">{{ importError }}</p>
  </div>
</template>
